import { useEffect, useState } from 'react';

/**
 * useTOCScrollProgress — track which lesson body sections the learner
 * has already scrolled past. Watches every `<h2 id="...">` inside the
 * given container via IntersectionObserver and returns a `Set` of the
 * heading ids that have gone above the top of the viewport (i.e. read).
 *
 * • `containerRef` — the outer div wrapping the rendered lesson body.
 * • `toc` — the array returned by `useLessonTOC` (needs .id per entry).
 *
 * Observer callback fires on scroll; a section is considered "read"
 * once its `<h2>` has left the top 25 % of the viewport. This is more
 * forgiving than requiring the WHOLE section to be scrolled past.
 */
const useTOCScrollProgress = (containerRef, toc) => {
  const [read, setRead] = useState(() => new Set());

  useEffect(() => {
    if (!containerRef?.current || !toc?.length) return undefined;
    const container = containerRef.current;

    // Grab the actual heading nodes each time the TOC changes so we
    // survive body re-renders (e.g. i18n retranslation, hot reload).
    const nodes = toc
      .map((h) => container.querySelector(`#${CSS.escape(h.id)}`))
      .filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        setRead((prev) => {
          const next = new Set(prev);
          let changed = false;
          for (const entry of entries) {
            const id = entry.target.id;
            if (!id) continue;
            // Heading has scrolled ABOVE the top 25 % band → mark read.
            if (!entry.isIntersecting && entry.boundingClientRect.top < 0) {
              if (!next.has(id)) { next.add(id); changed = true; }
            }
          }
          return changed ? next : prev;
        });
      },
      // Trigger the moment the heading is within the top 25 % of the
      // viewport, and again when it leaves upward.
      { rootMargin: '0px 0px -75% 0px', threshold: [0, 1] },
    );
    nodes.forEach((n) => observer.observe(n));

    // Prime the state on mount so a mid-page reload doesn't reset all
    // sections to unread when the learner is already 50 % down.
    const primed = new Set();
    for (const n of nodes) {
      if (n.getBoundingClientRect().top < 0) primed.add(n.id);
    }
    if (primed.size) setRead((prev) => {
      const merged = new Set(prev);
      primed.forEach((id) => merged.add(id));
      return merged;
    });

    return () => observer.disconnect();
    // Rerun when the TOC array identity or its length changes.
  }, [containerRef, toc]);

  return read;
};

export default useTOCScrollProgress;
